import cloneDeep from "lodash/cloneDeep";
import isEmpty from "lodash/isEmpty";
import isPlainObject from "lodash/isPlainObject";
import mergeWith from "lodash/mergeWith";

// Deep merge one or more objects.
// By default, lodash merges arrays by index:
//
//   merge({ models: ["A", "B"] }, { models: ["C"] })
//   => { models: ["C", "B"] }
//
// For application settings, arrays represent complete values rather than
// collections to merge, so an overriding array should replace the original
// array entirely.
//
// Examples:
//   app.settings.models      = ["A", "B"]
//   task.settings.models     = ["C"]
//   resolved.models          = ["C"]
//
export function deepMerge(...objects) {
    return mergeWith({}, ...objects, (objValue, srcValue) => {
        if (Array.isArray(objValue) || Array.isArray(srcValue)) {
            // Use the whole new array
            return srcValue;
        }

        return undefined;
    });
}

export function pruneObject(value, options = {}) {
    const {
        removeUndefined = true,
        removeNull = false,
        removeEmptyArrays = true,
        removeEmptyObjects = true,
    } = options;

    if (removeUndefined && value === undefined) return undefined;
    if (removeNull && value === null) return undefined;

    if (Array.isArray(value)) {
        const cleanedArray = value
            .map((item) => pruneObject(item, options))
            .filter((item) => item !== undefined);

        if (removeEmptyArrays && cleanedArray.length === 0) {
            return undefined;
        }

        return cleanedArray;
    }

    if (isPlainObject(value)) {
        const cleanedObject = Object.fromEntries(
            Object.entries(value)
                .map(([key, nestedValue]) => [
                    key,
                    pruneObject(nestedValue, options),
                ])
                .filter(([, nestedValue]) => nestedValue !== undefined),
        );

        if (removeEmptyObjects && isEmpty(cleanedObject)) {
            return undefined;
        }

        return cleanedObject;
    }

    return cloneDeep(value);
}
